import { Button } from '@creditornot/cb-button';
import { FormRow } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import arrayMutators from 'final-form-arrays';
import { uniqueId } from 'lodash';
import React, { useCallback } from 'react';
import { Form, useForm } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import * as yup from 'yup';

import { WoltModal } from 'common/components';
import validateYupSchema from 'common/utils/validateYupSchema';
import { useAddSubcategoriesTracking } from 'modules/menu/hooks/telemetryHooks';

import { AddSubcategoriesFormValues } from './types';
import { childCategoriesValidationSchema } from './validationSchema';
import { SubcategoryInputFields } from '../SubcategoryInputFields';
import { UNIQUEID_CATEGORY_PREFIX } from '../constants';

type BulkAddSubcategoryModalProps = {
  isOpen: boolean;
  onConfirm: (values: AddSubcategoriesFormValues['childCategoriesRecord'][string]) => void;
  onCancel: () => void;
};

type SubcategoryInputFieldsComponentProps = {
  categoryRowId: string;
  childCategoriesRecord: AddSubcategoriesFormValues['childCategoriesRecord'];
};

const categoryRowId = uniqueId(UNIQUEID_CATEGORY_PREFIX);

const initialValues: AddSubcategoriesFormValues = {
  childCategoriesRecord: {
    [categoryRowId]: [
      {
        name: '',
        _rowId: uniqueId(UNIQUEID_CATEGORY_PREFIX),
      },
    ],
  },
};

const AddSubcategoryFormFooter = styled(ModalFooter)``;

const CancelButton = styled(Button)`
  margin-inline-start: auto;
`;

const SaveButton = styled(Button)`
  margin-inline-start: 16px;
`;

const AddSubcategoryFormModalContent = styled(ModalContent)`
  overflow: auto;
  max-height: 600px;
`;

const Root = styled(WoltModal)`
  width: 700px;
  background: ${cbSemanticCssVars.surfaceMain};
  border-radius: 8px;
  overflow: hidden;
`;

const StyledFormRow = styled(FormRow)`
  padding: 0;
`;

const StyledInputAlert = styled(InputAlert)`
  margin-block-start: 8px;
`;

const SubcategoryInputFieldsComponent = ({
  categoryRowId,
  childCategoriesRecord,
}: SubcategoryInputFieldsComponentProps) => {
  const subcategories = childCategoriesRecord[categoryRowId];
  const form = useForm<AddSubcategoriesFormValues>();
  const { fields } = useFieldArray<AddSubcategoriesFormValues['childCategoriesRecord']>(
    `childCategoriesRecord[${categoryRowId}]`,
  );

  const handleChange = useCallback(
    (data: AddSubcategoriesFormValues['childCategoriesRecord'][string]) => {
      const updatedSubcategoriesRecord = { ...childCategoriesRecord, [categoryRowId]: data };
      form.change('childCategoriesRecord', updatedSubcategoriesRecord);
    },
    [categoryRowId, childCategoriesRecord, form],
  );

  return <SubcategoryInputFields fields={fields} subcategories={subcategories} onChange={handleChange} />;
};

export const BulkAddSubcategoryModal = ({ onCancel, onConfirm, isOpen }: BulkAddSubcategoryModalProps) => {
  const { formatMessage } = useIntl();
  const trackAddSubcategories = useAddSubcategoriesTracking();
  const handleSubmit = useCallback(
    (formValues: AddSubcategoriesFormValues) => {
      const subcategories = formValues.childCategoriesRecord[categoryRowId].filter(value => value.name.trim() !== '');
      trackAddSubcategories(
        categoryRowId,
        subcategories.map(category => category.name),
      );
      onConfirm(subcategories);
    },
    [onConfirm, trackAddSubcategories],
  );
  return (
    <Root isOpen={isOpen} onRequestClose={onCancel}>
      <ModalHeader onCloseClick={onCancel}>
        <FormattedMessage id="menu.subcategory.add-title" />
      </ModalHeader>

      <Form<AddSubcategoriesFormValues>
        mutators={{
          ...arrayMutators,
        }}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validate={validateYupSchema(
          yup.object({
            childCategoriesRecord: childCategoriesValidationSchema,
          }),
        )}
      >
        {({ handleSubmit, hasValidationErrors, submitting, values }) => (
          <>
            <AddSubcategoryFormModalContent>
              <StyledFormRow label={formatMessage({ id: 'menu.subcategory.name-input-label' })}>
                <SubcategoryInputFieldsComponent
                  categoryRowId={categoryRowId}
                  childCategoriesRecord={values.childCategoriesRecord}
                />
                <StyledInputAlert variant="info">
                  <FormattedMessage id="menu.subcategory.add-info" />
                </StyledInputAlert>
              </StyledFormRow>
            </AddSubcategoryFormModalContent>
            <AddSubcategoryFormFooter>
              <CancelButton size="small" variant="lightBlue" disabled={submitting} onClick={onCancel}>
                <FormattedMessage id="state.cancel" />
              </CancelButton>
              <SaveButton
                size="small"
                variant="blue"
                loading={submitting}
                disabled={hasValidationErrors || submitting}
                onClick={handleSubmit}
              >
                <FormattedMessage id="state.save" />
              </SaveButton>
            </AddSubcategoryFormFooter>
          </>
        )}
      </Form>
    </Root>
  );
};
